import { FaTrash } from 'react-icons/fa';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { DetailLink } from 'components/DetailsView';
import { SectionContainer } from 'components/DetailsView/Section';
import { TotemImage } from 'components/TotemImage';
import { TotemInput } from 'components/TotemInput';
import { TotemLabel } from 'components/TotemLabel';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemSelect } from 'components/TotemSelect';

import { GetDetailedProductQuery, HotDrinksIngredientUnit, useGetProductsWithRecipesQuery } from 'data/__generated__';

import { HOT_DRINKS_CATEGORY_ID, HOT_DRINKS_MACHINE_CATEGORY_ID } from 'pages/Products/constants/categories';

export type RecipeIngredient = {
    productId: string;
    quantity: number;
    hotDrinksIngredientUnit: HotDrinksIngredientUnit;
};

type RecipeFormData = {
    recipeIngredients: RecipeIngredient[];
};

type IngredientProduct = {
    _id: string;
    fullname: string;
    imageUrl?: string | null;
    weightWithoutPackaging?: number | null;
};

type DetailedProduct = NonNullable<GetDetailedProductQuery['detailedProduct']>;

// Le catalogue des ingrédients (tous les produits) n'est chargé qu'à l'ouverture de la section (`lazy`), et
// jamais pour un produit qui n'est pas une machine à boissons chaudes. `recipeIngredients` est dans les
// `defaultValues` du formulaire produit : la section peut ne jamais être montée sans rien perdre au save.
export const RecipeSection = ({ product }: { product: DetailedProduct }) => {
    if (product.category._id !== HOT_DRINKS_MACHINE_CATEGORY_ID) {
        return null;
    }

    return (
        <SectionContainer title="Recette machine à café" isInitiallyOpen={false} lazy>
            <RecipeContent product={product} />
        </SectionContainer>
    );
};

const RecipeContent = ({ product }: { product: DetailedProduct }) => {
    const { setValue, control, getValues } = useFormContext<RecipeFormData>();
    const ingredients = useWatch({ control, name: 'recipeIngredients' }) || [];

    const { data: productsData } = useGetProductsWithRecipesQuery();

    const availableIngredients =
        productsData?.getAllProducts.filter((p) => p.categoryId === HOT_DRINKS_CATEGORY_ID) || [];

    // Merge available ingredients with existing recipe ingredients for display
    const ingredientProductsById = new Map<string, IngredientProduct>(
        availableIngredients.map((product) => [
            product._id,
            {
                _id: product._id,
                fullname: product.fullname,
                imageUrl: product.imageUrl,
                weightWithoutPackaging: product.weightWithoutPackaging,
            },
        ]),
    );
    product.recipe?.ingredients.forEach((ing) => {
        if (ing.product && !ingredientProductsById.has(ing.product._id)) {
            ingredientProductsById.set(ing.product._id, ing.product);
        }
    });

    const getCurrentIngredients = (): RecipeIngredient[] => getValues('recipeIngredients') || [];

    const updateIngredients = (newIngredients: RecipeIngredient[]) => {
        setValue('recipeIngredients', newIngredients, { shouldDirty: true });
    };

    const addIngredient = (productId: string) => {
        const current = getCurrentIngredients();
        if (!current.some((ing) => ing.productId === productId)) {
            updateIngredients([
                ...current,
                { productId, quantity: 1, hotDrinksIngredientUnit: HotDrinksIngredientUnit.G },
            ]);
        }
    };

    const removeIngredient = (productId: string) => {
        updateIngredients(getCurrentIngredients().filter((ing) => ing.productId !== productId));
    };

    const updateIngredientQuantity = (productId: string, quantity: number) => {
        updateIngredients(
            getCurrentIngredients().map((ing) => (ing.productId === productId ? { ...ing, quantity } : ing)),
        );
    };

    const updateIngredientUnit = (productId: string, hotDrinksIngredientUnit: HotDrinksIngredientUnit) => {
        updateIngredients(
            getCurrentIngredients().map((ing) =>
                ing.productId === productId ? { ...ing, hotDrinksIngredientUnit } : ing,
            ),
        );
    };

    const availableIngredientsOptions = availableIngredients
        .filter(({ _id }) => !ingredients.some((ing) => ing.productId === _id))
        .map(({ _id, fullname }) => ({ value: _id, label: fullname }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const unitOptions = [
        { value: HotDrinksIngredientUnit.G, label: 'g' },
        { value: HotDrinksIngredientUnit.Unit, label: 'unit' },
    ];

    return (
        <Content>
            <SelectContainer>
                <TotemSelect
                    label="Ajouter un ingrédient"
                    placeholder="Sélectionner un ingrédient (chocolat, sucre, gobelet...)"
                    value={null}
                    options={availableIngredientsOptions}
                    onChange={(value) => {
                        if (value) {
                            addIngredient(value);
                        }
                    }}
                />
            </SelectContainer>

            <TotemLabel>Ingrédients de la recette</TotemLabel>
            {ingredients.length === 0 ? (
                <EmptyMessage>Aucun ingrédient ajouté</EmptyMessage>
            ) : (
                <IngredientsTable>
                    <thead>
                        <tr>
                            <TableHeaderCell />
                            <TableHeaderCell>Composant</TableHeaderCell>
                            <TableHeaderCell>Quantité par paquet (g)</TableHeaderCell>
                            <TableHeaderCell>Unité</TableHeaderCell>
                            <TableHeaderCell>Quantité</TableHeaderCell>
                            <TableHeaderCell />
                        </tr>
                    </thead>
                    <tbody>
                        {ingredients.map(({ productId, quantity, hotDrinksIngredientUnit }) => {
                            const ingredientProduct = ingredientProductsById.get(productId);
                            const fullname = ingredientProduct?.fullname || productId;
                            const weightInGrams = ingredientProduct?.weightWithoutPackaging
                                ? ingredientProduct.weightWithoutPackaging * 1000
                                : null;

                            return (
                                <tr key={productId}>
                                    <TableBodyCell>
                                        <TotemImage src={ingredientProduct?.imageUrl} alt={fullname} size={50} />
                                    </TableBodyCell>
                                    <TableBodyCell>
                                        <DetailLink name={fullname} value={productId} path="product" />
                                    </TableBodyCell>
                                    <TableBodyCell>{weightInGrams ?? '-'}</TableBodyCell>
                                    <TableBodyCell>
                                        <UnitSelectContainer>
                                            <TotemSelect
                                                placeholder="-"
                                                value={hotDrinksIngredientUnit}
                                                options={unitOptions}
                                                onChange={(value) => {
                                                    if (value) {
                                                        updateIngredientUnit(productId, value);
                                                    }
                                                }}
                                            />
                                        </UnitSelectContainer>
                                    </TableBodyCell>
                                    <TableBodyCell>
                                        <QuantityInput
                                            type="number"
                                            value={quantity}
                                            min={0.01}
                                            step={0.01}
                                            onChange={(value) =>
                                                updateIngredientQuantity(productId, parseFloat(value) || 0)
                                            }
                                        />
                                    </TableBodyCell>
                                    <TableBodyCell $isDataCentered>
                                        <TotemPrimaryButton type="button" onClick={() => removeIngredient(productId)}>
                                            <FaTrash size={20} />
                                        </TotemPrimaryButton>
                                    </TableBodyCell>
                                </tr>
                            );
                        })}
                    </tbody>
                </IngredientsTable>
            )}
        </Content>
    );
};

const Content = styled.div`
    padding: 15px;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const SelectContainer = styled.div``;

const EmptyMessage = styled.p`
    color: ${({ theme }) => theme.textColor};
    font-style: italic;
`;

const IngredientsTable = styled.table`
    width: 100%;
    margin-top: 10px;
    border-radius: ${({ theme }) => theme.borderRadius};
    border-spacing: 0px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const TableHeaderCell = styled.th<{ $isDataCentered?: boolean }>`
    padding: 10px;
    text-align: ${({ $isDataCentered }) => ($isDataCentered ? 'center' : 'left')};
`;

const TableBodyCell = styled.td<{ $isDataCentered?: boolean }>`
    padding: 10px;
    border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
    text-align: ${({ $isDataCentered }) => ($isDataCentered ? 'center' : 'left')};
`;

const QuantityInput = styled(TotemInput)`
    width: 100px;
`;

const UnitSelectContainer = styled.div`
    width: 100px;
`;
